import { Link } from 'react-router';
import { useCurrentUser } from '../hooks/useAuth';
import { useResource } from '../hooks/useResource';
import { api, errorMessage } from '../lib/api';
import { barStats, bookStats, formatCount } from '../lib/stats';

/** Signed-in home: a few fun numbers about the household's catalogues, and the way to add more. */
export function HomePage() {
  const user = useCurrentUser();
  const books = useResource('books', api.listBooks);
  const bottles = useResource('bottles', api.listBottles);
  const household = useResource('household', api.household);
  const firstName = user.name?.split(' ')[0];

  const loading = (books.data === null && books.loading) || (bottles.data === null && bottles.loading);
  const error = books.error ?? bottles.error;
  if (loading) return <p className="page-status">Loading…</p>;
  if (books.data === null || bottles.data === null) {
    return (
      <div className="page-status" role="alert">
        <p className="error-text">{errorMessage(error)}</p>
        <button type="button" className="btn btn--primary" onClick={books.data === null ? books.reload : bottles.reload}>
          Try again
        </button>
      </div>
    );
  }

  const b = bookStats(books.data);
  const d = barStats(bottles.data);
  const members = household.data?.members ?? [];
  const others = members.filter((m) => m.id !== user.id);
  const invites = household.data?.incoming.length ?? 0;

  return (
    <section className="home">
      <header className="page-header">
        <div>
          <h1>{firstName ? `Hello, ${firstName}` : 'Hello'}</h1>
          <p className="muted">
            {others.length > 0
              ? `Your household with ${others.map((m) => m.name?.split(' ')[0] ?? m.email).join(', ')}.`
              : 'Your catalogues, at a glance.'}
          </p>
        </div>
        <div className="home__actions">
          <Link className="btn btn--primary" to="/scan?scan=books">
            Scan books
          </Link>
          <Link className="btn btn--primary" to="/scan?scan=drinks">
            Scan bottles
          </Link>
        </div>
      </header>

      {invites > 0 && (
        <p className="notice">
          You have {invites === 1 ? 'an invitation' : `${invites} invitations`} to join a household.{' '}
          <Link to="/household">Have a look</Link>
        </p>
      )}

      <div className="home__grid">
        <Link className="stat-card" to="/collection">
          <h2>Books</h2>
          {b.books === 0 ? (
            <p className="muted">No books yet. Scan the barcode on the back of one to start.</p>
          ) : (
            <>
              <p className="stat-card__big">
                {formatCount(b.books)} <span className="stat-card__unit">{b.books === 1 ? 'book' : 'books'}</span>
              </p>
              <ul className="stat-card__facts">
                {b.pages > 0 && <li>About {formatCount(b.pages)} pages between them.</li>}
                {b.toBeRead > 0 && (
                  <li>
                    {formatCount(b.toBeRead)} waiting to be read{b.read > 0 ? `, ${formatCount(b.read)} read` : ''}.
                  </li>
                )}
                {b.toBeRead === 0 && b.read > 0 && <li>{formatCount(b.read)} marked as read.</li>}
                {b.wishlist > 0 && <li>{formatCount(b.wishlist)} on the wishlist.</li>}
                {b.topAuthor && (
                  <li>
                    Most collected: {b.topAuthor.name} ({b.topAuthor.count}).
                  </li>
                )}
                {b.biggestOwnCategory && (
                  <li>
                    Biggest shelf: {b.biggestOwnCategory.name} ({b.biggestOwnCategory.count}).
                  </li>
                )}
                {b.recent > 0 && <li>{b.recent} added in the last month.</li>}
              </ul>
            </>
          )}
        </Link>

        <Link className="stat-card" to="/bar">
          <h2>Bar</h2>
          {bottles.data.length === 0 ? (
            <p className="muted">Nothing in the bar yet. Scan a bottle's barcode to start.</p>
          ) : (
            <>
              <p className="stat-card__big">
                {formatCount(d.inStock)} <span className="stat-card__unit">in stock</span>
              </p>
              <ul className="stat-card__facts">
                {d.litres > 0 && <li>About {d.litres} litres on hand.</li>}
                {d.topKind && (
                  <li>
                    Mostly {d.topKind.label.toLowerCase()} ({d.topKind.count}).
                  </li>
                )}
                {d.open > 0 && <li>{d.open} open.</li>}
                {d.worthChecking > 0 && <li>{d.worthChecking} worth checking the level of.</li>}
                {d.shoppingList > 0 && <li>{d.shoppingList} on the shopping list.</li>}
                {d.finished > 0 && <li>{d.finished} finished and remembered.</li>}
              </ul>
            </>
          )}
        </Link>

        <Link className="stat-card" to="/household">
          <h2>Household</h2>
          {others.length === 0 ? (
            <p className="muted">Just you. Invite the people you live with and you'll share one collection.</p>
          ) : (
            <p className="muted">
              {members.length} of you share this collection: {members.map((m) => m.name ?? m.email).join(', ')}.
            </p>
          )}
        </Link>
      </div>
    </section>
  );
}
